'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangleIcon } from '@/components/icons';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// Признаки протухшей вкладки после деплоя — клиент не должен видеть UI ошибки,
// его нужно сразу автоматически перезагрузить со сбросом кэша.
const STALE_DEPLOY_PATTERN =
  /Failed to find Server Action|ChunkLoadError|Loading (CSS )?chunk \d+ failed|Loading chunk [\w-]+ failed/i;

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    console.error('Application Error:', error);

    if (STALE_DEPLOY_PATTERN.test(error.message || '')) {
      const w = window as unknown as { __aziaRecover?: (reason: string) => void };
      if (typeof w.__aziaRecover === 'function') {
        w.__aziaRecover('error-boundary:' + (error.message || '').slice(0, 80));
        return;
      }
      try {
        const u = new URL(location.href);
        u.searchParams.set('_cb', String(Date.now()));
        location.replace(u.toString());
      } catch {
        location.reload();
      }
    }
  }, [error]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center space-y-6 max-w-md">
        {/* Error icon */}
        <div className="w-20 h-20 mx-auto rounded-pill bg-destructive/10 flex items-center justify-center">
          <AlertTriangleIcon weight="bold" className="w-10 h-10 text-destructive" />
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
            Что-то пошло не так
          </h1>
          <p className="text-muted-foreground">
            Произошла ошибка при загрузке страницы. Попробуйте обновить страницу
            или вернуться позже.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
          <button
            onClick={() => reset()}
            className="px-6 py-3 rounded-card bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            Попробовать снова
          </button>
          <Link
            href="/"
            className="px-6 py-3 rounded-card border border-border text-foreground font-medium hover:bg-muted transition-colors"
          >
            На главную
          </Link>
        </div>

        {/* Error details (dev only) */}
        {process.env.NODE_ENV === 'development' && error.message && (
          <div className="mt-8 p-4 rounded-card bg-muted text-left">
            <p className="text-xs font-mono text-muted-foreground break-all">
              {error.message}
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
